import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import ReviewIllustration from 'Assets/banner/review-illustration.png';
import {
  makeGetFeatureFlag,
  makeSelectCommercialField,
  makeSelectConfigField,
  selectIsFirstPageView,
} from 'Containers/App/selectors';
import { makeGetRecommendedHsaContribution } from 'Containers/CommercialRoutes/selectors';
import { makeSelectHsaField } from 'Containers/HsaPage/selectors';
import {
  makeGetSupplementalPremiums,
  makeGetHsaPlan,
  makeGetHraPlan,
  makeSelectOverviewField,
} from 'Containers/OverviewPage/selectors';
import {
  makeGetBuilderGoSettings,
  makeGetEligibleEnrollmentPlan,
  makeGetEnrollmentPlans,
  makeGetDataRevisionLocation,
  makeGetSelectedPublicationPlanYear,
} from 'Containers/ProfilePage/selectors';
import { ContentfulTextNoteType, selectAlexGoNotes } from 'ContentfulDefaults/types/_shared';
import { ContentfulInfoBars } from 'ContentfulWrappers/ContentfulInfoBars';
import { ContentfulTextNote } from 'ContentfulWrappers/ContentfulTextNote';
import { ContentfulHeaderWrapper } from 'ContentfulWrappers/header';
import { useTextContext, CLIENT_MODEL_CONTENT_TYPES } from 'Contexts/textContext';
import { Button, Paragraph } from 'DesignLibrary/atoms';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import { BannerCard } from 'DesignLibrary/molecules/BannerCard';
import Text from 'Shared/Text';
import {
  JVPEnrollmentPlan,
  Recommendation,
  SelectedSupplementalPlans,
  SupplementalPlan,
  SupplementalPlansFullHousehold,
} from 'Types/entities';
import { BuilderGoSettings, EligibleEnrollmentPlan } from 'Utils/apiTypes';
import { sendInsightsEvent } from 'Utils/insights';
import { RESULT_PATH, HSA_PLUS_PATH, OVERVIEW_PATH, REVIEW_PATH } from 'Utils/urls';

import { reviewPageLoaded, submitFeedback } from './actions';
import Feedback from './Feedback';
import NextSteps from './NextSteps';
import ReviewSection from './ReviewSection';
import { makeSelectReviewPageField } from './selectors';
import { GridContainer } from './styled';

function deriveUrlFromPlan(plan: JVPEnrollmentPlan | EligibleEnrollmentPlan | undefined): string {
  if (!plan) return '';
  if (plan.has_enrollment_site && plan.enrollment_site_url) return plan.enrollment_site_url;
  if (plan.has_info_site && plan.info_site_is_enrollment_site && plan.info_site_url)
    return plan.info_site_url;
  return '';
}

export function resolveEnrollmentUrl(
  eligibleEnrollmentPlan: EligibleEnrollmentPlan | null,
  bootstrapEnrollmentPlans: JVPEnrollmentPlan[],
  isEnrollmentPlanEligibilityEnabled: boolean | null,
): string {
  if (isEnrollmentPlanEligibilityEnabled) {
    if (eligibleEnrollmentPlan) {
      // Server has determined the eligible plan — use its URL exactly; do not fall back to bootstrap.
      // If the plan has no usable URL the button is not shown.
      return deriveUrlFromPlan(eligibleEnrollmentPlan);
    }
    // No server-computed plan yet (built-in questions flow): fall back to bootstrap
    return deriveUrlFromPlan(bootstrapEnrollmentPlans[0]);
  }
  return deriveUrlFromPlan(bootstrapEnrollmentPlans[0]);
}

interface ReviewPageStateProps {
  healthPlan: Recommendation | Record<string, never>;
  supplementalPlans: SupplementalPlansFullHousehold;
  selectedSupplementalPlans: SelectedSupplementalPlans;
  supplementalPremiums: number;
  bootstrapEnrollmentPlans: JVPEnrollmentPlan[];
  recommendedHsaContribution: number;
  lockedHsaContribution: number | null;
  hsaPlan: SupplementalPlan | null;
  hraPlan: SupplementalPlan | null;
  sihraContribution: number;
  feedbackSubmitted: boolean;
  isFbsEnabled: boolean;
  isFirstPageView: boolean;
  builderGoSettings: BuilderGoSettings | null;
  planYear?: number;
  eligibleEnrollmentPlan: EligibleEnrollmentPlan | null;
  isEnrollmentPlanEligibilityEnabled: boolean | null;
  dataRevisionLocation: string | null;
}

interface ReviewPageDispatchProps {
  setReviewPageLoaded: () => void;
  handleFeedbackSubmit: () => void;
}

export type ReviewPageProps = ReviewPageStateProps & ReviewPageDispatchProps;

export const ReviewPage = ({
  healthPlan,
  hsaPlan,
  hraPlan,
  sihraContribution,
  supplementalPremiums,
  supplementalPlans,
  selectedSupplementalPlans,
  bootstrapEnrollmentPlans,
  recommendedHsaContribution,
  lockedHsaContribution,
  // employerHraContribution,
  feedbackSubmitted,
  isFbsEnabled,
  isFirstPageView,
  setReviewPageLoaded,
  handleFeedbackSubmit,
  builderGoSettings,
  planYear,
  eligibleEnrollmentPlan,
  isEnrollmentPlanEligibilityEnabled,
  dataRevisionLocation,
}: ReviewPageProps) => {
  const resolvedEnrollmentUrl = resolveEnrollmentUrl(
    eligibleEnrollmentPlan,
    bootstrapEnrollmentPlans,
    isEnrollmentPlanEligibilityEnabled,
  );
  const navigate = useNavigate();
  const { retrieveContentfulData } = useTextContext();

  const alexGoReviewNotes = retrieveContentfulData<ContentfulTextNoteType[]>(
    CLIENT_MODEL_CONTENT_TYPES.alex_go_review_note,
  );
  const selectedAlexGoReviewNotes = selectAlexGoNotes(
    alexGoReviewNotes,
    { planYear, upcomingPlanYear: undefined },
    false,
  );

  useEffect(() => {
    setReviewPageLoaded();
  }, []);

  const trackPrint = (e: React.MouseEvent<HTMLButtonElement>) => {
    sendInsightsEvent(e, 'print_results');

    window.print();
  };

  const trackEnroll = () => {
    sendInsightsEvent(null, 'enroll_health');
  };

  const handleSupplementalChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    sendInsightsEvent(e, 'return_to_fbs');

    navigate(OVERVIEW_PATH);
  };

  const handleHealthChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    sendInsightsEvent(e, 'return_to_health');

    navigate(RESULT_PATH);
  };

  const handleHsaChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    sendInsightsEvent(e, 'return_to_hsa');

    navigate(HSA_PLUS_PATH);
  };

  return (
    <div id="review-container">
      <ContentfulHeaderWrapper sectionKey="review_section" animate={isFirstPageView} />
      <ContentfulInfoBars sectionKey="review_section">
        {selectedAlexGoReviewNotes.map((note) => (
          <ContentfulTextNote key={note.plan_year} note={note} />
        ))}
      </ContentfulInfoBars>
      <PageLayout page="review">
        <GridContainer>
          <section className="main-content">
            <ReviewSection
              healthPlan={healthPlan}
              hsaPlan={hsaPlan}
              hraPlan={hraPlan}
              sihraContribution={sihraContribution}
              supplementalPlans={supplementalPlans}
              selectedSupplementalPlans={selectedSupplementalPlans}
              supplementalPremiums={supplementalPremiums}
              // both here and the HSA insights event are using this `if null`
              // logic, we should refactor this to use a selector for both,
              // and eventually the ben admin enrollment logic as well
              recommendedHsaContribution={
                lockedHsaContribution !== null ? lockedHsaContribution : recommendedHsaContribution
              }
              isFbsEnabled={isFbsEnabled}
              handleSupplementalChange={handleSupplementalChange}
              handleHealthChange={handleHealthChange}
              handleHsaChange={handleHsaChange}
              builderGoSettings={builderGoSettings}
              dataRevisionLocation={dataRevisionLocation}
            />
            <NextSteps enrollmentUrl={resolvedEnrollmentUrl} trackEnroll={trackEnroll} />
          </section>
          <aside className="review-sidebar">
            <BannerCard imgUrl={ReviewIllustration}>
              <Paragraph size="small">
                <Text field="review_section.lets_enroll_card_text" />
              </Paragraph>
              {resolvedEnrollmentUrl && (
                <Button
                  className="enroll-btn"
                  url={resolvedEnrollmentUrl}
                  onClick={trackEnroll}
                  buttonType="primary"
                  iconRight="ArrowUpRight"
                  stretch
                >
                  <Text field="review_section.button_text.enroll" />
                </Button>
              )}
              <Button
                onClick={(e) => trackPrint(e)}
                buttonType="transparent"
                size="small"
                iconRight="Printer"
                stretch
              >
                <Text field="review_section.button_text.PDF" />
              </Button>
            </BannerCard>
            <Feedback feedbackSubmitted={feedbackSubmitted} handleFeedbackSubmit={handleFeedbackSubmit} />
          </aside>
        </GridContainer>
      </PageLayout>
    </div>
  );
};

const mapStateToProps = () =>
  createStructuredSelector<GlobalReducerState, ReviewPageStateProps>({
    healthPlan: makeSelectCommercialField('selectedHealthPlan'),
    supplementalPlans: makeSelectOverviewField('supplemental_plans_full_household'),
    selectedSupplementalPlans: makeSelectOverviewField('selected_plans'),
    supplementalPremiums: makeGetSupplementalPremiums(),
    bootstrapEnrollmentPlans: makeGetEnrollmentPlans(),
    recommendedHsaContribution: makeGetRecommendedHsaContribution(),
    lockedHsaContribution: makeSelectHsaField('lockedHsaContribution'),
    hsaPlan: makeGetHsaPlan(),
    hraPlan: makeGetHraPlan(),
    sihraContribution: makeSelectCommercialField('annual_sihra_contribution'),
    feedbackSubmitted: makeSelectReviewPageField('feedbackSubmitted'),
    isFbsEnabled: makeSelectConfigField('is_fbs_enabled'),
    isFirstPageView: selectIsFirstPageView(REVIEW_PATH),
    builderGoSettings: makeGetBuilderGoSettings(),
    planYear: makeGetSelectedPublicationPlanYear(),
    eligibleEnrollmentPlan: makeGetEligibleEnrollmentPlan(),
    isEnrollmentPlanEligibilityEnabled: makeGetFeatureFlag('is_enrollment_plan_eligibility_enabled'),
    dataRevisionLocation: makeGetDataRevisionLocation(),
  });

export function mapDispatchToProps(dispatch): ReviewPageDispatchProps {
  return {
    setReviewPageLoaded: () => dispatch(reviewPageLoaded()),
    handleFeedbackSubmit: () => dispatch(submitFeedback()),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(ReviewPage);
